// Single source of truth for runtime-platform detection in the web app.
//
// The two device axes -- **form factor** and **platform** -- are reported by the
// native shell. Historically they arrived fused together in `appStyles`, a list
// of CSS class tokens, and this module existed to pick the platform back out of
// them. That was always the wrong carrier: "styles" is a fact about Logbook's
// presentation, not about the device, and the tokens were the only encoding of
// two facts nothing else modelled cleanly.
//
// #1376 (tracker #1362) inverts it. The shell reports `formFactor` and `platform` directly
// (app-base's `Device`, @mutle's decision), and the app derives its own style
// tokens from them via `deriveAppStyles`. Sniffing `appStyles` remains as the
// fallback while the shipped shells are unmigrated -- coexistence is by message,
// so both paths stay live and the legacy one stays primary until a shell moves.
//
// Every platform check in the app should go through the `Platform` object rather
// than testing `appStyles.includes(...)` directly, so there is one consistent
// code path for iOS, Android and the desktop shells:
//
//   Platform.isIOS(app)   // boolean
//   Platform.of(app)      // "web" | "ios" | "android" | "windows" | "mac"
//   FormFactor.of(app)    // "desktop" | "tablet" | "mobile" | undefined

export type PlatformName = "web" | "ios" | "android" | "windows" | "mac";

// The physical interaction class. Deliberately NOT extended with a "web" member:
// a browser session has no device to have a form factor, and `undefined` says
// "no shell answered" -- the distinction the whole bridge design is built to keep
// expressible. Guessing "desktop" from a wide window would be a different claim.
export type FormFactorName = "desktop" | "tablet" | "mobile";

const PLATFORM_NAMES: PlatformName[] = ["ios", "android", "windows", "mac"];
const FORM_FACTOR_NAMES: FormFactorName[] = ["desktop", "tablet", "mobile"];

// Accept anything carrying the axes. `appStyles` is still required because it is
// the live path for every shipped shell; the two axes are optional because only a
// migrated shell reports them.
interface PlatformSource {
  appStyles: string[];
  platform?: string;
  formFactor?: string;
}

function of(app: PlatformSource): PlatformName {
  // A shell that reports the axis is the authority on it, so the reported value
  // wins over the tokens. Values outside this vocabulary -- `LogbookDeviceAxes`
  // also reports `linux`, `logbookBox` and `unknown` -- collapse to "web", which
  // is both the plain-browser answer and what token-sniffing already did by
  // falling through.
  const resolved = resolvePlatform({ platform: app.platform, formFactor: app.formFactor });
  if (resolved.platform !== undefined) {
    return (PLATFORM_NAMES as string[]).includes(resolved.platform) ? resolved.platform as PlatformName : "web";
  }
  for (const name of PLATFORM_NAMES) {
    if (app.appStyles.includes(name)) {
      return name;
    }
  }
  return "web";
}

function formFactorOf(app: PlatformSource): FormFactorName | undefined {
  // Unlike `platform`, an unrecognised value is not collapsed to a default --
  // there is no sensible "some other form factor", so an unknown one is reported
  // as "no answer" rather than silently becoming a desktop.
  const resolved = resolvePlatform({ platform: app.platform, formFactor: app.formFactor });
  if (resolved.formFactor !== undefined) {
    return (FORM_FACTOR_NAMES as string[]).includes(resolved.formFactor) ? resolved.formFactor as FormFactorName : undefined;
  }
  return FORM_FACTOR_NAMES.find((name) => app.appStyles.includes(name));
}

// The `app-style-<token>` CSS classes `RootView` applies, derived from the axes
// rather than delivered as a list. Mirrors `LogbookDeviceAxes.appStyles(for:)` on
// the Swift side; the two are pinned by tests on both sides so they cannot drift.
//
// `["default"]` is the no-shell answer and is load-bearing rather than a
// sentinel: `.app-style-default` is a real rule in `RootView.scss`.
export function deriveAppStyles(axes: { formFactor?: string; platform?: string }): string[] {
  const formFactor = axes.formFactor;
  const platform = axes.platform;
  if (formFactor === undefined || platform === undefined || platform === "web") {
    return ["default"];
  }
  return [formFactor, platform];
}

export const Platform = {
  of,
  isIOS: (app: PlatformSource): boolean => of(app) === "ios",
  isAndroid: (app: PlatformSource): boolean => of(app) === "android",
  isWindows: (app: PlatformSource): boolean => of(app) === "windows",
  isMac: (app: PlatformSource): boolean => of(app) === "mac",
  // A native desktop/mobile shell (anything other than a plain browser / cloud
  // session).
  isNativeApp: (app: PlatformSource): boolean => of(app) !== "web",
};

export const FormFactor = {
  of: formFactorOf,
  isDesktop: (app: PlatformSource): boolean => formFactorOf(app) === "desktop",
  isTablet: (app: PlatformSource): boolean => formFactorOf(app) === "tablet",
  isMobile: (app: PlatformSource): boolean => formFactorOf(app) === "mobile",
};
import { resolvePlatform } from "app-base/runtime/platform"
